// src/components/EntityTagsSection.jsx
import React, { useState, useEffect, useCallback, useId } from 'react';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal';
import HelpIcon from './Documentation/HelpIcon';

const TrashIcon = ({ className = 'h-4 w-4' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const PencilIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
);

function mergeNewTagIntoList(prevTags, rawTag) {
    const nextTag = (rawTag || '').trim();
    if (!nextTag) return { nextTags: prevTags, changed: false };
    if (prevTags.some(tag => tag.toLowerCase() === nextTag.toLowerCase())) {
        return { nextTags: prevTags, changed: false };
    }
    return { nextTags: [...prevTags, nextTag], changed: true };
}

/**
 * @param {object} props
 * @param {string[]|null|undefined} props.tags
 * @param {(nextTags: string[]) => Promise<void>} props.onPersistTags
 * @param {() => Promise<string[]>} props.loadTagSuggestions
 * @param {string} props.documentationId
 * @param {string} [props.sectionTitle]
 * @param {string} props.modalTitle
 * @param {string} props.persistErrorMessage
 */
function EntityTagsSection({
    tags: tagsProp,
    onPersistTags,
    loadTagSuggestions,
    documentationId,
    sectionTitle = 'Tags',
    modalTitle,
    persistErrorMessage
}) {
    const tags = Array.isArray(tagsProp) ? tagsProp : [];
    const hasTags = tags.length > 0;
    const datalistId = useId();

    const [isTagEditModalOpen, setIsTagEditModalOpen] = useState(false);
    const [editedTags, setEditedTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [availableTagSuggestions, setAvailableTagSuggestions] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const list = await loadTagSuggestions();
                if (!cancelled && Array.isArray(list)) {
                    setAvailableTagSuggestions(list);
                }
            } catch (err) {
                console.error('Error loading tag suggestions:', err);
                if (!cancelled) setAvailableTagSuggestions([]);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [loadTagSuggestions]);

    const openTagEditModal = useCallback(() => {
        setEditedTags(tags);
        setTagInput('');
        setIsTagEditModalOpen(true);
    }, [tags]);

    const mergeSuggestions = useCallback((newTags) => {
        setAvailableTagSuggestions(prev =>
            Array.from(new Set([...prev, ...newTags])).sort((a, b) => a.localeCompare(b))
        );
    }, []);

    const persistAndMerge = useCallback(async (tagsToSave) => {
        await onPersistTags(tagsToSave);
        mergeSuggestions(tagsToSave);
    }, [onPersistTags, mergeSuggestions]);

    const closeTagEditModal = useCallback(async () => {
        let tagsToSave = editedTags;
        const inputTag = tagInput.trim();
        if (inputTag) {
            const { nextTags } = mergeNewTagIntoList(editedTags, inputTag);
            tagsToSave = nextTags;
            setEditedTags(nextTags);
            setTagInput('');
        }
        try {
            await persistAndMerge(tagsToSave);
        } catch (err) {
            console.error('Error persisting tags:', err);
            alert(persistErrorMessage);
            return;
        }
        setIsTagEditModalOpen(false);
    }, [tagInput, editedTags, persistAndMerge, persistErrorMessage]);

    const addTagToEditList = useCallback(async (rawTag) => {
        const nextTag = (rawTag || '').trim();
        if (!nextTag) return;
        let tagsToPersist = null;
        setEditedTags(prevTags => {
            const { nextTags, changed } = mergeNewTagIntoList(prevTags, rawTag);
            if (changed) tagsToPersist = nextTags;
            return nextTags;
        });
        setTagInput('');
        if (!tagsToPersist) return;
        try {
            await persistAndMerge(tagsToPersist);
        } catch (err) {
            console.error('Error persisting tags:', err);
            alert(persistErrorMessage);
        }
    }, [persistAndMerge, persistErrorMessage]);

    const removeTagFromEditList = useCallback((tagToRemove) => {
        setEditedTags(prevTags => prevTags.filter(tag => tag !== tagToRemove));
    }, []);

    const handleTagInputKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addTagToEditList(tagInput);
        }
    }, [addTagToEditList, tagInput]);

    const handleDeleteTagDirect = useCallback(async (tagToDelete) => {
        const updatedTags = tags.filter(tag => tag !== tagToDelete);
        try {
            await persistAndMerge(updatedTags);
        } catch (err) {
            console.error('Error deleting tag:', err);
            alert(persistErrorMessage);
        }
    }, [tags, persistAndMerge, persistErrorMessage]);

    return (
        <>
            <Card>
                <div className="p-4">
                    <div className="flex justify-between items-center mb-3 pb-2 border-b">
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-gray-700">{sectionTitle}</h3>
                            <HelpIcon documentationId={documentationId} position="relative" />
                        </div>
                        {!hasTags ? (
                            <Button
                                variant="secondary"
                                className="text-xs py-1 px-2"
                                onClick={openTagEditModal}
                            >
                                Add Tag
                            </Button>
                        ) : (
                            <Button
                                variant="secondary"
                                className="p-1 text-gray-500 hover:text-gray-700"
                                onClick={openTagEditModal}
                                title="Edit tags"
                            >
                                <PencilIcon />
                            </Button>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {tags.map(tag => (
                            <span
                                key={tag}
                                className="group inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                title={tag}
                            >
                                <span>{tag}</span>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteTagDirect(tag)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700"
                                    title={`Delete tag ${tag}`}
                                >
                                    <TrashIcon className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                        {!hasTags && (
                            <p className="text-sm text-gray-500 italic w-full">No tags assigned yet.</p>
                        )}
                    </div>
                </div>
            </Card>

            <Modal isOpen={isTagEditModalOpen} onClose={closeTagEditModal} title={modalTitle}>
                <div>
                    <div>
                        <label htmlFor={`entity-tag-input-${datalistId}`} className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                        <Input
                            id={`entity-tag-input-${datalistId}`}
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={handleTagInputKeyDown}
                            placeholder="Enter to add a tag; OK saves and closes"
                            list={datalistId}
                            autoFocus
                        />
                        <datalist id={datalistId}>
                            {availableTagSuggestions.map(s => (
                                <option key={s} value={s} />
                            ))}
                        </datalist>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {editedTags.length > 0 ? (
                                editedTags.map(tag => (
                                    <button
                                        type="button"
                                        key={tag}
                                        onClick={() => removeTagFromEditList(tag)}
                                        className="px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                                        title="Click to remove"
                                    >
                                        {tag} ×
                                    </button>
                                ))
                            ) : (
                                <p className="text-xs text-gray-500 italic">No tags selected.</p>
                            )}
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <Button type="button" variant="primary" onClick={() => closeTagEditModal()}>
                            OK
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default EntityTagsSection;
